import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import Table from "../../../../core/common/dataTable/index";
import CommonSelect from "../../../../core/common/commonSelect";
import FeeShareModals from "../courseFeeShareModals";
import { useCourseFeeShare } from "../hooks/useCourseFeeShare";

// Filter options
const shareTypeOptions = [
  { value: "", label: "All Types" },
  { value: "fixed", label: "Fixed Amount (₹)" },
  { value: "percent", label: "Percentage (%)" },
];

const sessionOptions = [
  { value: "", label: "All Sessions" },
  { value: "Session 2023-24", label: "Session 2023-24" },
  { value: "Session 2022-23", label: "Session 2022-23" },
  { value: "Session 2021-22", label: "Session 2021-22" },
];

const CourseFeeShareList = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const {
    feeShares,
    paginationData,
    isLoading,
    isError,
    error,
    handlePageChange,
    handlePageSizeChange,
    handleSearch,
    searchTerm,
    handleFilterChange,
    refetch,
  } = useCourseFeeShare();

  const [selectedFeeShareId, setSelectedFeeShareId] = useState<string | null>(
    null
  );

  const handleDeleteClick = (id: number) => {
    setSelectedFeeShareId(id.toString());
  };

  const handleAddFeeShareClick = () => {
    navigate(routes.addCourseFeeShare);
  };

  const [filterValues, setFilterValues] = useState({
    share_type: "",
    session_id: undefined as number | undefined,
    all_centers: false,
  });

  const handleFilterValueChange = (field: string, value: any) => {
    setFilterValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    handleFilterChange({
      share_type: filterValues.share_type || undefined,
      session_id: filterValues.session_id,
    });

    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  const handleResetFilters = () => {
    setFilterValues({
      share_type: "",
      session_id: undefined,
      all_centers: false,
    });
    // Reset the actual filters
    handleFilterChange({
      share_type: undefined,
      session_id: undefined,
    });
  };

  // Table columns definition
  const columns = [
    {
      title: "S. No",
      render: (_: any, __: any, index: number) => index + 1,
      sorter: (a: any, b: any) => a.id - b.id,
    },
    {
      title: "Session",
      dataIndex: "session_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <Link
              to={all_routes.viewCourseFeeShare.replace(
                ":id",
                record.id.toString()
              )}
              className="text-primary mb-0"
            >
              {text}
            </Link>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.session_name.localeCompare(b.session_name),
    },
    {
      title: "Course",
      dataIndex: "course_name",
      render: (text: string) => <div>{text}</div>,
      sorter: (a: any, b: any) => a.course_name.localeCompare(b.course_name),
    },
    {
      title: "Fee Type",
      dataIndex: "fee_type",
      render: (text: string) => <div>Course Fee</div>,
    },
    {
      title: "Share Type",
      dataIndex: "share_type",
      render: (text: string) => (
        <span
          className={`badge badge-soft-${
            text === "fixed" ? "primary" : "success"
          }`}
        >
          {text === "fixed" ? "Fixed" : "Percentage"}
        </span>
      ),
      sorter: (a: any, b: any) => a.share_type.localeCompare(b.share_type),
    },
    {
      title: "Action",
      render: (record: any) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to={all_routes.viewCourseFeeShare.replace(
                      ":id",
                      record.id.toString()
                    )}
                  >
                    <i className="ti ti-eye me-2" />
                    View Details
                  </Link>
                </li>
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to={all_routes.editCourseFeeShare.replace(
                      ":id",
                      record.id.toString()
                    )}
                  >
                    <i className="ti ti-edit me-2" />
                    Edit
                  </Link>
                </li>
                <li>
                  <Link
                    to="#"
                    className="dropdown-item rounded-1"
                    data-bs-toggle="modal"
                    data-bs-target="#delete-modal"
                    onClick={() => handleDeleteClick(record.id)}
                  >
                    <i className="ti ti-trash me-2" />
                    Delete
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Course Fee Shares</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">Fee Shares</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Course Fee Shares
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <button
                  onClick={handleAddFeeShareClick}
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Course Fee Share
                </button>
              </div>
            </div>
          </div>

          {/* Fee Shares List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Course Fee Shares List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 pb-0 border-bottom">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Share Type</label>
                              <CommonSelect
                                className="select"
                                options={shareTypeOptions}
                                value={shareTypeOptions.find(
                                  (option) =>
                                    option.value === filterValues.share_type
                                )}
                                onChange={(selected) => {
                                  if (selected) {
                                    handleFilterValueChange(
                                      "share_type",
                                      selected.value
                                    );
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Session</label>
                              <CommonSelect
                                className="select"
                                options={sessionOptions}
                                value={sessionOptions.find(
                                  (option) =>
                                    option.value ===
                                    filterValues.session_id?.toString()
                                )}
                                onChange={(selected) => {
                                  if (selected) {
                                    handleFilterValueChange(
                                      "session_id",
                                      selected.value
                                        ? parseInt(selected.value, 10)
                                        : undefined
                                    );
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">All Centers</label>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="allCenters"
                                  checked={filterValues.all_centers}
                                  onChange={(e) =>
                                    handleFilterValueChange(
                                      "all_centers",
                                      e.target.checked
                                    )
                                  }
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="allCenters"
                                >
                                  Only show fee shares for all centers
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <button
                          type="button"
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleApplyFilters}
                        >
                          Apply
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by A-Z
                  </Link>
                  <ul className="dropdown-menu p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1 active">
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Descending
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Oldest First
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading course fee shares...</p>
                </div>
              ) : isError ? (
                <div className="alert alert-danger m-3">
                  Error loading course fee shares: {error?.toString()}
                </div>
              ) : (
                <Table
                  dataSource={feeShares}
                  columns={columns}
                  rowKey="id"
                  onSearchChange={handleSearch}
                  pagination={{
                    pageSize: paginationData.pageSize,
                    current: paginationData.currentPage,
                    total: paginationData.totalItems,
                    onChange: (page: number, pageSize?: number) => {
                      handlePageChange(page);
                      if (pageSize) {
                        handlePageSizeChange(pageSize);
                      }
                    },
                    pageSizeOptions: ["All", 10, 20, 30],
                  }}
                />
              )}
            </div>
          </div>
          {/* /Fee Shares List */}
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Use the separate modal component instead of inline modal */}
      <FeeShareModals
        feeShareId={selectedFeeShareId || undefined}
        onSuccess={() => {
          refetch();
          setSelectedFeeShareId(null);
        }}
      />
    </>
  );
};

export default CourseFeeShareList;
